import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
    AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import CallsList from './CallsList';
import { useAppSelector } from "@store/hooks";
import bookingCycleMgr from '../../../../../../../services/bookingCycleMgr';

const name = 'CollapsibleCallsList';

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const isToday = (date: Date) => {
    const today = new Date();
    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
}

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

interface CollapsibleCallsListProps {
}

const CollapsibleCallsList: React.FC<CollapsibleCallsListProps> = () => {
    const [expanded, setExpanded] = React.useState<string | false>('today');
    const [today, setToday] = React.useState<any[]>([]);
    const [past, setPast] = React.useState<any[]>([]);

    const callsHistory = useAppSelector(({ temp }) => temp.CallsHistory);

    React.useEffect(() => {
        const today = callsHistory.filter(
            (b) => isToday(new Date(b.dte_start!)) && !bookingCycleMgr.hasSessionId(b.internal_iq_session_id!)
        );
        const past = callsHistory.filter(
            (b) => !isToday(new Date(b.dte_start!))
        );
        setToday(today);
        setPast(past);
    }, [callsHistory]);

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    return (
        <Box>
            {today.length > 0 &&
                <Accordion expanded={expanded === "today"} onChange={handleChange("today")}>
                    <AccordionSummary aria-controls="completed-content" id="completed-header">
                        <Typography>{`Calls Today (${today.length})`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CallsList calls={today} type="today" />
                    </AccordionDetails>
                </Accordion>
            }
            {past.length > 0 &&
                <Accordion expanded={expanded === "past"} onChange={handleChange("past")}>
                    <AccordionSummary aria-controls="progressed-content" id="progress-header">
                        <Typography>{`Calls In Past (${past.length})`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CallsList calls={past} type="past" />
                    </AccordionDetails>
                </Accordion>
            }
        </Box>
    );
}

CollapsibleCallsList.displayName = name;

export default React.memo(CollapsibleCallsList);